/**
 * THE SHELL'S SEARCH AND SHARE DEFAULTS, written once (2026-09-30).
 *
 * public/index.html is served for every path, and its head carries these tags so a link preview that runs no
 * JavaScript (most of them) still gets a title, a description and a picture. Every page that writes its own head
 * (src/components/SeoHead.tsx, src/components/ForLawyersHead.tsx) writes the same tags again. react-helmet-async
 * replaces only the tags it manages, the ones carrying `data-rh`, so each such page carried two of each, the site
 * default first. Measured on live prest0.ai on 2026-09-30: /about, /for-enterprise and /for-individuals carried two
 * descriptions, two og:titles, two og:images and two of every twitter tag, and a reader that takes the first tag got
 * the site default instead of the page's own.
 *
 * Now the shell marks exactly these tags `data-rh="true"`, so the head manager treats them as its own, and
 * src/components/SiteDefaultHead.tsx, mounted FIRST inside the one HelmetProvider (src/index.tsx), writes them again
 * from this list. A page's own head, mounted after it, overrides any of them; the rest keep the default. So every tag
 * is said once: the page's value where it has one, the default where it has none, after any number of in-app moves.
 * A preview that never runs JavaScript still reads the shell's copies, which are unchanged.
 *
 * tests/landing/eachHeadTagIsSaidOnce.test.tsx holds this list equal to the tags the shell marks, and the build's
 * layout check (scripts/check-public-pages-layout.mjs) counts every page's head tags in a real browser.
 */
import { BRAND_SHARE_IMAGE_URL } from './brandMark.generated';

export const SITE_DEFAULT_TITLE = 'prest0 — More Expert Capacity, Without More Headcount';
export const SITE_DEFAULT_DESCRIPTION =
  'Prest0n is an AI employee that does anything an employee with a computer can do, with unlimited human seats: for law, marketing, finance, real estate, insurance, property management and any business.';
export const SITE_DEFAULT_KEYWORDS =
  'prest0, Prest0n, AI employee, full-time AI employee, unlimited human seats, law, marketing, finance, real estate, insurance, property management';
export const SITE_DEFAULT_OG_TYPE = 'website';
export const SITE_DEFAULT_SHARE_IMAGE = BRAND_SHARE_IMAGE_URL;
export const SITE_DEFAULT_TWITTER_CARD = 'summary_large_image';
export const SITE_DEFAULT_TWITTER_HANDLE = '@prest0ai';

export interface SiteDefaultTag {
  /** The attribute that names the tag: `name` or `property`. */
  attribute: 'name' | 'property';
  key: string;
  content: string;
}

/** The tags the shell marks for the head manager, in the order public/index.html writes them. */
export const SITE_DEFAULT_TAGS: readonly SiteDefaultTag[] = [
  { attribute: 'name', key: 'description', content: SITE_DEFAULT_DESCRIPTION },
  { attribute: 'name', key: 'keywords', content: SITE_DEFAULT_KEYWORDS },
  { attribute: 'property', key: 'og:type', content: SITE_DEFAULT_OG_TYPE },
  { attribute: 'property', key: 'og:title', content: SITE_DEFAULT_TITLE },
  { attribute: 'property', key: 'og:description', content: SITE_DEFAULT_DESCRIPTION },
  { attribute: 'property', key: 'og:image', content: SITE_DEFAULT_SHARE_IMAGE },
  { attribute: 'name', key: 'twitter:card', content: SITE_DEFAULT_TWITTER_CARD },
  { attribute: 'name', key: 'twitter:title', content: SITE_DEFAULT_TITLE },
  { attribute: 'name', key: 'twitter:description', content: SITE_DEFAULT_DESCRIPTION },
  { attribute: 'name', key: 'twitter:image', content: SITE_DEFAULT_SHARE_IMAGE },
  { attribute: 'name', key: 'twitter:creator', content: SITE_DEFAULT_TWITTER_HANDLE },
  { attribute: 'name', key: 'twitter:site', content: SITE_DEFAULT_TWITTER_HANDLE },
];
